/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {forwardRef, useEffect, useState} from 'react';
import {Accordion, AccordionDetails} from '@material-ui/core';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {MaterialExpandMoreIcon} from '@ion/dls/src/icons/MaterialExpandMoreIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';

// defines the icon positions for the ExpansionPanel
export enum IconPosition {
  Start = 'start',
  End = 'end'
};

// defines the prop definitions for the ExpansionPanel
export interface ExpansionPanelTypes {
  id: string;
  header: any;
  subHeader?: any;
  onToggleExpand: (event: object, expanded: boolean) => void;
  expandedLayer?: string | null;
  details: any;
  summaryRootClass?: string;
  rootClass?: string;
  detailsRootOverride?: string;
  disableExpansion?: boolean;
  unmountOnExit?: boolean;
  iconPosition?: IconPosition;
};

// defines the styles used by the ExpansionPanel
const useStyles = makeStyles(theme => ({
  wrapperRoot : {
    width           : '100%',
    boxShadow       : '0px 0px !important',
    borderRadius    : '0px !important',
    backgroundColor : 'none !important',
    marginTop       : '0px !important'
  },
  summaryContent : {
    margin : '0 !important'
  },
  detailsRoot : {
    display : 'block',
    padding : '10px 0px 25px'
  },
  expanded : {
    borderBottom : `solid 1px ${dlsColor('grey6')}`
  },
  expandIcon : {
    order : -1
  }
}));

const isIconAtStart = (position: IconPosition) => position === IconPosition.Start;

/**
 * @ExpansionPanel
 *
 * Defines the ExpansionPanel component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 *   import Accordion from '@ion/dls/src/components/accordion/Accordion';
 *   <Accordion children=''
 *     id={}
 *     header={}
 *     details={} />
 * ```
 */
const ExpansionPanelWrapper = forwardRef(({
  id,
  header,
  subHeader,
  details,
  onToggleExpand,
  expandedLayer,
  summaryRootClass,
  rootClass,
  detailsRootOverride,
  iconPosition = IconPosition.End,
  disableExpansion = false,
  unmountOnExit = false
}: ExpansionPanelTypes, ref) => {
  const [expanded, setExpanded] = useState(false);
  const classes = useStyles();

  const handleChange = (panel) => function({target}, isExpanded) {
    if (!disableExpansion) {
      onToggleExpand(panel, isExpanded);
    }
  };

  useEffect(() => {
    setExpanded(expandedLayer === id);
  }, [expandedLayer]);

  return (
    <Accordion expanded={expanded} TransitionProps={{unmountOnExit}}
      ref={ref}
      classes={{
        root : classnames(classes.wrapperRoot, rootClass)
      }}
      onChange={handleChange(id)}>
      <AccordionSummary expandIcon={!disableExpansion && <MaterialExpandMoreIcon />}
        IconButtonProps={isIconAtStart(iconPosition) ? {
          edge : 'start'
        } : null}
        classes={{
          root       : classnames(expanded && classes.expanded, summaryRootClass),
          content    : classes.summaryContent,
          expandIcon : isIconAtStart(iconPosition) ? classes.expandIcon : null
        }}>
        {header}
        {subHeader}
      </AccordionSummary>
      <AccordionDetails classes={{
        root : detailsRootOverride || classes.detailsRoot
      }}>
        {details}
      </AccordionDetails>
    </Accordion>
  );
});

export default ExpansionPanelWrapper;
